/**
 * ManagedAgentLivePanel
 *
 * Renders the streaming `liveText` produced by Managed Agents (v2). Sits
 * above the existing activity log on the Activity tab. Super-admin gated by
 * the parent component during the pilot.
 *
 * The text is streamed by managed/eventBridge.ts → summarizationTasks.liveText
 * which Convex propagates reactively, so this component just renders state.
 */

import { Loader2, Sparkles } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";

interface ManagedAgentLivePanelProps {
  liveText: string;
  isRunning: boolean;
  anthropicAgentId?: string;
  anthropicAgentVersion?: number;
  anthropicSessionId?: string;
  runnerVersion?: string;
}

export function ManagedAgentLivePanel({
  liveText,
  isRunning,
  anthropicAgentId,
  anthropicAgentVersion,
  anthropicSessionId,
  runnerVersion,
}: ManagedAgentLivePanelProps) {
  return (
    <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/40 dark:border-violet-900/50 dark:bg-violet-950/10">
      <div className="flex items-center justify-between gap-3 border-b border-violet-200/60 px-4 py-2 dark:border-violet-900/40">
        <div className="flex items-center gap-2 text-sm font-medium text-violet-900 dark:text-violet-100">
          <Sparkles className="h-3.5 w-3.5" />
          <span>Managed Agent — live stream</span>
          {isRunning && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-500" />
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          {runnerVersion && (
            <Badge variant="secondary" className="font-mono">
              runner {runnerVersion}
            </Badge>
          )}
          {anthropicAgentVersion !== undefined && (
            <Badge variant="secondary" className="font-mono">
              v{anthropicAgentVersion}
            </Badge>
          )}
        </div>
      </div>

      <div className="max-h-72 overflow-y-auto px-4 py-3">
        {liveText ? (
          <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-foreground/80">
            {liveText}
            {isRunning && (
              <span className="-mb-0.5 ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-violet-500" />
            )}
          </pre>
        ) : (
          <p className="text-xs italic text-muted-foreground">
            {isRunning
              ? "Waiting for the agent to send its first tokens..."
              : "No streaming text from the last run."}
          </p>
        )}
      </div>

      {(anthropicAgentId || anthropicSessionId) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 border-t border-violet-200/60 px-4 py-2 font-mono text-[10px] text-muted-foreground dark:border-violet-900/40">
          {anthropicAgentId && <span>agent: {anthropicAgentId}</span>}
          {anthropicSessionId && <span>session: {anthropicSessionId}</span>}
        </div>
      )}
    </div>
  );
}
